import { csrfToken, jsonRequest } from '@/utils/jsonRequest'
import { router } from '@inertiajs/svelte'

type AnalyticsPage = {
  url: string
  component: string
  props: Record<string, unknown>
}

// No vendor SDK or content capture. Rails owns identity, sessions, attribution,
// exclusions and delivery; this only observes pages actually shown by Inertia.
export function initializeAnalytics(initialPage: AnalyticsPage): () => void {
  let currentPage = initialPage
  let lastUrl: string | undefined
  let signupStarted = false
  let pending = Promise.resolve()

  const send = (event: '$pageview' | 'signup_started') => {
    if (currentPage.props.analytics_enabled !== true) return
    if (!csrfToken()) return
    const body = { event, page: currentPage.component }
    // Serialize requests so the browser applies session-cookie updates before
    // the next event. Failures never interfere with the application.
    pending = pending.then(async () => {
      try {
        await jsonRequest('/analytics/events', {
          method: 'POST',
          keepalive: true,
          signal: AbortSignal.timeout(3000),
          body,
        })
      } catch {
        /* Analytics is best-effort, including offline browsing. */
      }
    })
  }

  const visit = (page: AnalyticsPage) => {
    currentPage = page
    const url = page.url.split('#')[0]
    if (url === lastUrl) return
    lastUrl = url
    signupStarted = false
    send('$pageview')
  }

  const startSignup = () => {
    if (
      signupStarted ||
      !['registrations/new', 'oauth/registrations/new'].includes(
        currentPage.component
      )
    )
      return
    signupStarted = true
    send('signup_started')
  }

  const stopNavigation = router.on('navigate', (event) =>
    visit(event.detail.page)
  )
  document.addEventListener('input', startSignup)
  document.addEventListener('submit', startSignup)
  visit(initialPage)

  return () => {
    stopNavigation()
    document.removeEventListener('input', startSignup)
    document.removeEventListener('submit', startSignup)
  }
}
